import globalAxios from './axiosInstance';

const API_GATEWAY_BASE_URL = process.env.REACT_APP_BLINKPAY_API_GATEWAY_URL;

const camelToSnake = (str: string): string => {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

const snakeToCamel = (str: string): string => {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

const convertKeysToSnakeCase = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (obj instanceof Date) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => convertKeysToSnakeCase(item));
    }

    if (typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const snakeKey = camelToSnake(key);
            acc[snakeKey] = convertKeysToSnakeCase(obj[key]);
            return acc;
        }, {} as any);
    }

    return obj;
};

const convertKeysToCamelCase = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (obj instanceof Date) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => convertKeysToCamelCase(item));
    }

    if (typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const camelKey = snakeToCamel(key);
            acc[camelKey] = convertKeysToCamelCase(obj[key]);
            return acc;
        }, {} as any);
    }

    return obj;
};

export const createQuickPayment = async (data: any) => {
    try {
        const snakeCaseData = convertKeysToSnakeCase(data);
        const response = await globalAxios.post(`${API_GATEWAY_BASE_URL}/payments/v1/quick-payments`, snakeCaseData);
        console.log('Quick payment triggered successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error triggering quick payment:', error);
        throw error;
    }
};

export const createSingleConsent = async (data: any) => {
    try {
        const snakeCaseData = convertKeysToSnakeCase(data);
        const response = await globalAxios.post(`${API_GATEWAY_BASE_URL}/payments/v1/single-consents`, snakeCaseData);
        console.log('Single consent triggered successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error triggering single consent:', error);
        throw error;
    }
};

export const createEnduringConsent = async (data: any) => {
    try {
        const snakeCaseData = convertKeysToSnakeCase(data);
        const response = await globalAxios.post(`${API_GATEWAY_BASE_URL}/payments/v1/enduring-consents`, snakeCaseData);
        console.log('Enduring consent triggered successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error triggering enduring consent:', error);
        throw error;
    }
};

export const createPayment = async (data: any) => {
    try {
        const snakeCaseData = convertKeysToSnakeCase(data);
        const response = await globalAxios.post(`${API_GATEWAY_BASE_URL}/payments/v1/payments`, snakeCaseData);
        console.log('Payment triggered successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error triggering payment:', error);
        throw error;
    }
};

export interface Invoice {
    invoiceId: string;
    invoiceNumber: string;
    onlineInvoiceUrl: string;
}

// A one-cent Xero invoice from the demo org, reused while it is unpaid. Nothing is
// emailed and nothing is sent: the shopper pays it from the online invoice URL.
export const createInvoice = async (): Promise<Invoice> => {
    try {
        const response = await globalAxios.post(`${API_GATEWAY_BASE_URL}/invoices`, {});
        const invoice: Invoice = convertKeysToCamelCase(response.data);
        console.log('Invoice created successfully:', invoice.invoiceNumber);
        return invoice;
    } catch (error: any) {
        console.error('Error creating invoice, status:', error?.response?.status ?? 'unknown');
        throw error;
    }
};

// Stripe checkout. The Idempotency-Key is the browser's per-attempt UUID; the BFF
// passes it through unchanged so a retry never creates a second payment.
export const startBlinkPayCheckout = async (amount: any, idempotencyKey: string): Promise<{redirectUri: string}> => {
    try {
        const response = await globalAxios.post(
            `${API_GATEWAY_BASE_URL}/checkout/blinkpay`,
            convertKeysToSnakeCase({amount}),
            {headers: {'Idempotency-Key': idempotencyKey}}
        );
        console.log('BlinkPay checkout started successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error starting BlinkPay checkout:', error);
        throw error;
    }
};

export const createStripePaymentIntent = async (amount: any, idempotencyKey: string): Promise<{clientSecret: string}> => {
    try {
        const response = await globalAxios.post(
            `${API_GATEWAY_BASE_URL}/checkout/stripe-intent`,
            convertKeysToSnakeCase({amount}),
            {headers: {'Idempotency-Key': idempotencyKey}}
        );
        console.log('Stripe payment intent created successfully');
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error creating Stripe payment intent:', error);
        throw error;
    }
};

// The customer is back from their bank. The BFF asks Blink for the outcome (the
// query parameters on the redirect are not authoritative) and records it in Stripe.
export type BlinkPayCheckoutOutcome = {
    quickPaymentId: string,
    outcome: 'paid' | 'failed' | 'pending',
    paymentRecordId?: string
};

export const getBlinkPayCheckoutOutcome = async (quickPaymentId: string): Promise<BlinkPayCheckoutOutcome> => {
    try {
        const response = await globalAxios.get(
            `${API_GATEWAY_BASE_URL}/checkout/blinkpay/${encodeURIComponent(quickPaymentId)}`
        );
        console.log('BlinkPay checkout outcome retrieved successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error retrieving BlinkPay checkout outcome:', error);
        throw error;
    }
};

export const getEnduringConsent = async (consentId: string) => {
    try {
        const response = await globalAxios.get(
            `${API_GATEWAY_BASE_URL}/payments/v1/enduring-consents/${consentId}`
        );
        console.log('Enduring consent retrieved successfully:', response.data);
        return convertKeysToCamelCase(response.data);
    } catch (error) {
        console.error('Error retrieving enduring consent:', error);
        throw error;
    }
};